import { Button, ButtonGroup } from '@blueprintjs/core';
import cx from 'classnames';
import { useAppSelector } from 'hooks';
import React, { useEffect, useState } from 'react';

import { getEmptyScore } from 'utils';
import {
  LONG_LEAD_ROUTES,
  MAX_HOLDS_IN_LEAD_ROUTE,
  MAX_HOLDS_IN_LONG_LEAD_ROUTE,
  MAX_TIME_ON_LEAD_ROUTE,
  MAX_TRIES_ON_BOULDER_ROUTE,
} from '../../../constants';
import styles from './RouteScorer.module.scss';

type Props = {
  route: number;
  climber: Climber;
  loading: boolean;
  onScoreUpdate: (score: RouteScore) => Promise<void>;
};

function formatClimbingTime(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${minutes < 10 ? '0' + minutes : minutes}:${
    secs < 10 ? '0' + secs : secs
  }`;
}

export function LeadRouteScorer({
  route,
  climber,
  loading,
  onScoreUpdate,
}: Props) {
  const [climbing, setClimbing] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [editingTime, setEditingTime] = useState(false);

  const [draftTime, setDraftTime] = useState(0);

  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout>();

  const currentScoreFromState = useAppSelector(
    (state) => state.scoring.scores['lead'][route]?.[climber.id],
  );

  const currentScore =
    currentScoreFromState ?? getEmptyScore('lead', climber.id, route);

  const notStarted = !climbing && currentScore.time === 0;

  const postClimbingEditMode = !climbing && currentScore.time > 0;

  const maxHoldCount = LONG_LEAD_ROUTES.includes(route)
    ? MAX_HOLDS_IN_LONG_LEAD_ROUTE
    : MAX_HOLDS_IN_LEAD_ROUTE;

  useEffect(() => {
    if (editingTime) {
      setDraftTime(elapsedTime);
    }
  }, [editingTime]);

  useEffect(() => {
    if (currentScoreFromState && elapsedTime === 0) {
      setElapsedTime(currentScoreFromState!.time);
    }
  }, [currentScoreFromState]);

  useEffect(() => {
    if (elapsedTime === MAX_TIME_ON_LEAD_ROUTE) {
      setClimbing(false);
      navigator.vibrate?.(200);
      setScore({});
    }
  }, [elapsedTime]);

  useEffect(() => {
    if (timerInterval) {
      clearInterval(timerInterval);
    }

    if (climbing) {
      setTimerInterval(
        setInterval(
          () => setElapsedTime((elapsedTime) => elapsedTime + 1),
          1000,
        ),
      );
    }
  }, [climbing]);

  useEffect(() => {
    return () => {
      console.log('clearing interval');

      if (timerInterval) clearInterval(timerInterval);
    };
  }, []);

  useEffect(() => {
    setResetConfirm(false);
  }, [climbing, currentScore]);

  const resetScore = () => {
    setScore(getEmptyScore('lead', climber.id, route));
    setElapsedTime(0);
  };

  const setScore = async (scoreUpdate: Partial<LeadRouteScore>) => {
    // Got final hold, stop the timer
    if (
      scoreUpdate.holdCount === maxHoldCount &&
      currentScore.holdCount === maxHoldCount - 1
    ) {
      setClimbing(false);
    }

    const updatedScore = {
      ...currentScore,
      time: elapsedTime,
      ...scoreUpdate,
    };

    if (scoreUpdate.plus) {
      updatedScore.minus = false;
    } else if (scoreUpdate.minus) {
      updatedScore.plus = false;
    }

    await onScoreUpdate(updatedScore);
  };

  return (
    <div className={styles.scorer}>
      <div className={styles.scorer__row}>
        <div className={styles.scorer__lead__title}>
          <span>Holds -&nbsp;</span>
          <span className={styles.attempts}>
            {currentScoreFromState ? currentScore.holdCount : '-'}
          </span>
        </div>
      </div>
      <div className={styles.scorer__row}>
        <div className={styles.leadHoldsButtons}>
          <Button
            loading={loading}
            disabled={loading || currentScore.holdCount === 0 || notStarted}
            onClick={() => setScore({ holdCount: currentScore.holdCount - 1 })}
            icon="minus"
            large
            className={styles.button}
          />

          <Button
            disabled={
              loading ||
              currentScore.holdCount === maxHoldCount ||
              notStarted
            }
            loading={loading}
            onClick={() => setScore({ holdCount: currentScore.holdCount + 1 })}
            icon="plus"
            large
            className={styles.button}
          />
        </div>
      </div>

      <div className={styles.scorer__row_checkboxes}>
        <div className={styles.checkbox__with_label}>
          <span className={styles.checkbox_label}>-0.1</span>
          <input
            type="checkbox"
            disabled={loading || notStarted}
            className={styles.checkbox}
            checked={currentScore.minus}
            onChange={() => setScore({ minus: !currentScore.minus })}
          />
        </div>
        <div className={styles.checkbox__with_label}>
          <span className={styles.checkbox_label}>+0.1</span>
          <input
            type="checkbox"
            disabled={loading || notStarted}
            className={styles.checkbox}
            checked={currentScore.plus}
            onChange={() => setScore({ plus: !currentScore.plus })}
          />
        </div>
      </div>
      <div className={styles.scorer__timer_row}>
        <Button
          className={cx(styles.scorer__reset_button)}
          disabled={climbing || notStarted}
          onClick={() => {
            if (editingTime) {
              setEditingTime(false);
              return;
            }
            if (resetConfirm) {
              resetScore();
            }
            setResetConfirm(!resetConfirm);
          }}
        >
          {editingTime ? 'Cancel' : resetConfirm ? 'Reset?' : 'Reset'}
        </Button>

        <div
          className={cx([
            styles.scorer__timer,
            postClimbingEditMode && styles.editable__timer,
            editingTime && styles.editing__timer,
          ])}
          onClick={() => {
            if (postClimbingEditMode) {
              setEditingTime(true);
            }
          }}
        >
          {editingTime ? (
            <>
              <input
                className={styles.timeInput}
                value={'0' + Math.floor(draftTime / 60) || ''}
                placeholder="00"
                onChange={(e) => {
                  const newMinutesValue =
                    parseInt(
                      e.target.value
                        ?.replace(/[\D]/, '')
                        .replace(/^0+/, '')
                        .slice(0, 2),
                    ) || 0;

                  if (
                    newMinutesValue > Math.floor(MAX_TIME_ON_LEAD_ROUTE / 60)
                  ) {
                    return;
                  }

                  const newDraftTime = newMinutesValue * 60 + (draftTime % 60);

                  setDraftTime(
                    newDraftTime > MAX_TIME_ON_LEAD_ROUTE
                      ? MAX_TIME_ON_LEAD_ROUTE
                      : newDraftTime,
                  );
                }}
              />
              {':'}
              <input
                autoFocus
                className={styles.timeInput}
                placeholder="00"
                value={draftTime % 60 || ''}
                onChange={(e) => {
                  let newSecondsValue =
                    parseInt(
                      e.target.value
                        ?.replace(/[\D]/, '')
                        .replace(/^0+/, '')
                        .slice(0, 2),
                    ) || 0;

                  if (newSecondsValue > 59) {
                    return;
                  }

                  const newDraftTime =
                    Math.floor(draftTime / 60) * 60 + newSecondsValue;

                  if (newDraftTime > MAX_TIME_ON_LEAD_ROUTE) {
                    return;
                  }

                  setDraftTime(newDraftTime);
                }}
              />
            </>
          ) : (
            formatClimbingTime(elapsedTime)
          )}
        </div>

        <Button
          className={cx(
            styles.scorer__timer_button,
            climbing && styles.button__running,
          )}
          disabled={
            !editingTime && !climbing && elapsedTime >= MAX_TIME_ON_LEAD_ROUTE
          }
          onClick={() => {
            if (editingTime) {
              setScore({ time: draftTime });
              setElapsedTime(draftTime);
              setEditingTime(false);
              return;
            }

            if (climbing) {
              // Trigger time update when stop pressed
              setScore({});
            }
            setClimbing(!climbing);
          }}
        >
          {editingTime
            ? 'Save'
            : climbing
              ? 'Stop'
              : notStarted
                ? 'Start'
                : 'Continue'}
        </Button>
      </div>
    </div>
  );
}

export default function BoulderRouteScorer({
  route,
  climber,
  loading,
  onScoreUpdate,
}: Props) {
  const currentScoreFromState = useAppSelector(
    (state) => state.scoring.scores['boulder'][route]?.[climber.id],
  );

  const currentScore =
    currentScoreFromState ?? getEmptyScore('boulder', climber.id, route);

  const setScore = async (scoreUpdate: Partial<BoulderRouteScore>) => {
    const newScore = {
      ...currentScore,
      ...scoreUpdate,
    };

    // sync topped-zoned to attempts

    if (scoreUpdate.topped) {
      if (newScore.topAttempts === 0) newScore.topAttempts = 1;

      if (!newScore.zoned) {
        newScore.zoned = true;

        if (newScore.zoneAttempts === 0) newScore.zoneAttempts = 1;
      }
    }

    if (scoreUpdate.zoned !== undefined) {
      if (scoreUpdate.zoned) {
        if (newScore.zoneAttempts === 0) newScore.zoneAttempts = 1;
        if (newScore.topAttempts === 0) newScore.topAttempts = 1;
      } else {
        newScore.topped = false;
      }
    }

    const notClimbed = !newScore.topped && !newScore.zoned;

    const moreZonesThanTops = newScore.zoneAttempts > newScore.topAttempts;

    const updatedZoneAttempts = scoreUpdate.zoneAttempts !== undefined;
    const updatedTopAttempts = scoreUpdate.topAttempts !== undefined;

    if (updatedZoneAttempts) {
      if (notClimbed || moreZonesThanTops) {
        newScore.topAttempts = newScore.zoneAttempts;
      }
    } else if (updatedTopAttempts) {
      if (notClimbed || moreZonesThanTops) {
        newScore.zoneAttempts = newScore.topAttempts;
      }
    }

    // sync attempts to zoned/topped

    if (newScore.topAttempts === 0) {
      newScore.topped = false;
    }

    if (newScore.zoneAttempts === 0) {
      newScore.zoned = false;
      newScore.topped = false;
      newScore.topAttempts = 0;
    }

    await onScoreUpdate(newScore);
  };

  return (
    <div className={styles.scorer}>
      <div className={styles.scorer__row}>
        <div className={styles.scorer__boulder__title}>
          <span>T</span>
          <span className={styles.attempts}>
            {currentScoreFromState ? currentScore.topAttempts : '-'}
          </span>
        </div>
        <ButtonGroup>
          <Button
            disabled={loading || currentScore.topAttempts === 0}
            onClick={() =>
              setScore({ topAttempts: currentScore.topAttempts - 1 })
            }
            icon="minus"
            large
            className={styles.button}
          />

          <Button
            disabled={
              loading || currentScore.topAttempts === MAX_TRIES_ON_BOULDER_ROUTE
            }
            loading={loading}
            onClick={() =>
              setScore({ topAttempts: currentScore.topAttempts + 1 })
            }
            icon="plus"
            large
            className={styles.button}
          />
        </ButtonGroup>
        <input
          type="checkbox"
          disabled={loading}
          className={styles.checkbox}
          checked={currentScore.topped}
          onChange={() => setScore({ topped: !currentScore.topped })}
        />
      </div>
      <div className={styles.scorer__row}>
        <div className={styles.scorer__boulder__title}>
          <span>Z</span>
          <span className={styles.attempts}>
            {currentScoreFromState ? currentScore.zoneAttempts : '-'}
          </span>
        </div>
        <ButtonGroup>
          <Button
            disabled={loading || currentScore.zoneAttempts === 0}
            onClick={() =>
              setScore({ zoneAttempts: currentScore.zoneAttempts - 1 })
            }
            icon="minus"
            large
            className={styles.button}
          />
          <Button
            disabled={
              loading ||
              currentScore.zoneAttempts === MAX_TRIES_ON_BOULDER_ROUTE
            }
            loading={loading}
            onClick={() =>
              setScore({ zoneAttempts: currentScore.zoneAttempts + 1 })
            }
            icon="plus"
            large
            className={styles.button}
          />
        </ButtonGroup>
        <input
          type="checkbox"
          disabled={loading}
          className={styles.checkbox}
          checked={currentScore.zoned}
          onChange={() => setScore({ zoned: !currentScore.zoned })}
        />
      </div>
    </div>
  );
}
